import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Home,
  FileText,
  Database,
  Store,
  TrendingUp,
  ClipboardList,
  Calendar,
  BookMarked,
  SlidersHorizontal,
  MoreHorizontal,
  X,
} from "lucide-react";

const PRIMARY_ITEMS = [
  { path: "/feed", label: "Feed", icon: Home },
  { path: "/docs", label: "Documents", icon: FileText },
  { path: "/vendors", label: "Vendors", icon: Store },
  { path: "/insights", label: "Insights", icon: TrendingUp },
];

const MORE_ITEMS = [
  { path: "/database", label: "Database", icon: Database },
  { path: "/surveys", label: "Surveys", icon: ClipboardList },
  { path: "/events", label: "Events", icon: Calendar },
  { path: "/resources", label: "Resources", icon: BookMarked },
  { path: "/settings", label: "Settings", icon: SlidersHorizontal },
];

const MORE_PATHS = MORE_ITEMS.map((item) => item.path);

const MobileBottomNav = () => {
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname]);

  const moreActive = MORE_PATHS.some((p) => location.pathname.startsWith(p));

  return (
    <>
      {moreOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/30 md:hidden"
          onClick={() => setMoreOpen(false)}
          aria-hidden="true"
        />
      )}

      {moreOpen && (
        <div
          role="dialog"
          aria-label="More navigation"
          className="fixed bottom-16 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-2xl rounded-t-2xl md:hidden animate-in slide-in-from-bottom-4 duration-200"
        >
          <div className="flex items-center justify-between px-4 pt-4 pb-2">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">
              More
            </p>
            <button
              onClick={() => setMoreOpen(false)}
              className="p-1.5 rounded-lg hover:bg-gray-100"
              aria-label="Close more menu"
            >
              <X className="h-4 w-4 text-gray-500" />
            </button>
          </div>
          <nav className="px-2 pb-4 grid grid-cols-3 gap-1">
            {MORE_ITEMS.map(({ path, label, icon: Icon }) => (
              <NavLink
                key={path}
                to={path}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center gap-1.5 py-3 rounded-xl transition-colors ${
                    isActive
                      ? "bg-amber-50 text-amber-700"
                      : "text-gray-700 hover:bg-gray-50"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon
                      size={20}
                      className={isActive ? "text-amber-600" : "text-gray-500"}
                    />
                    <span className="text-[11px] font-medium">{label}</span>
                  </>
                )}
              </NavLink>
            ))}
          </nav>
        </div>
      )}

      <nav
        className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 md:hidden"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        aria-label="Primary"
      >
        <div className="flex items-stretch justify-around h-16">
          {PRIMARY_ITEMS.map(({ path, label, icon: Icon }) => (
            <NavLink
              key={path}
              to={path}
              aria-label={label}
              className={({ isActive }) =>
                `flex flex-1 flex-col items-center justify-center gap-0.5 text-[11px] font-medium transition-colors ${
                  isActive ? "text-amber-700" : "text-gray-500"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon
                    size={22}
                    className={isActive ? "text-amber-600" : "text-gray-500"}
                  />
                  <span>{label}</span>
                </>
              )}
            </NavLink>
          ))}

          <button
            onClick={() => setMoreOpen((v) => !v)}
            aria-label="More"
            aria-expanded={moreOpen}
            className={`flex flex-1 flex-col items-center justify-center gap-0.5 text-[11px] font-medium transition-colors ${
              moreActive || moreOpen ? "text-amber-700" : "text-gray-500"
            }`}
          >
            <MoreHorizontal
              size={22}
              className={
                moreActive || moreOpen ? "text-amber-600" : "text-gray-500"
              }
            />
            <span>More</span>
          </button>
        </div>
      </nav>
    </>
  );
};

export default MobileBottomNav;
